'use client';

import { useState, useEffect, useCallback, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { MomentoContext, Memo } from './MomentoContext';

interface MomentoProviderProps {
  children: ReactNode;
}

export function MomentoProvider({ children }: MomentoProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('');
  const [selectedMoment, setSelectedMoment] = useState<any>(null);
  const [activeMemo, setActiveMemo] = useState<Memo | null>(null);
  const pathname = usePathname();

  // Update current page when pathname changes
  useEffect(() => {
    setCurrentPage(pathname || '');
  }, [pathname]);

  // Keyboard shortcut (Cmd+M / Ctrl+M)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'm') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }

      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  // Exit memo mode: clear activeMemo and return to normal view
  const exitMemoMode = useCallback(() => {
    setActiveMemo(null);
  }, []);

  // Generate context-aware greeting based on current page
  const greeting = useCallback(() => {
    if (pathname?.includes('/dashboard/moments')) {
      return "I can help you find moments from any of your videos.";
    }
    if (pathname?.includes('/dashboard/posts')) {
      return "I can help you find moments from any of your videos.";
    }
    if (pathname?.includes('/dashboard/collections')) {
      return "Want to organize moments? I can help create collections.";
    }
    return "Hi! I'm Momento. What moments are you looking for?";
  }, [pathname])();

  const value = {
    isOpen,
    open,
    close,
    toggle,
    currentPage,
    setCurrentPage,
    greeting,
    selectedMoment,
    setSelectedMoment,
    activeMemo,
    setActiveMemo,
    exitMemoMode,
  };

  return (
    <MomentoContext.Provider value={value}>
      {children}
    </MomentoContext.Provider>
  );
}
